import PropTypes from 'prop-types';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import TaskCard from './TaskCard';
import { useThemeStore } from '@store';

export default function KanbanColumn({ id, title, color, tasks, onAddTask, onTaskClick, isDropDisabled = false }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  // Determine visual styles based on drop state and theme
  const getColumnStyles = () => {
    if (isDropDisabled) {
      return isDark
        ? 'bg-red-900/30 ring-2 ring-red-500/50 opacity-70'
        : 'bg-red-50 ring-2 ring-red-300/50 opacity-70';
    }
    if (isOver) {
      return isDark
        ? 'bg-slate-700/50 ring-2 ring-primary-500/40'
        : 'bg-slate-100 ring-2 ring-primary-500/30';
    }
    return '';
  };

  const columnBgClass = isDark ? 'bg-slate-800/60' : 'bg-slate-50';
  const titleClass = isDark ? 'text-slate-200' : 'text-slate-700';
  const blockedBgClass = isDark ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600';
  const buttonClass = isDark
    ? 'border-slate-600 text-slate-500 hover:border-slate-500 hover:text-slate-400'
    : 'border-slate-300 text-slate-400 hover:border-slate-400 hover:text-slate-500';

  return (
    <div
      className={`min-w-[280px] flex-shrink-0 ${columnBgClass} rounded-2xl p-4 transition-colors relative ${getColumnStyles()}`}
    >
      {/* Blocked indicator */}
      {isDropDisabled && (
        <div className={`absolute top-2 right-2 ${blockedBgClass} text-xs px-2 py-1 rounded-md flex items-center gap-1`}>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
          </svg>
          Bloqueado
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: color }}
          />
          <h3 className={`text-sm font-bold ${titleClass}`}>{title}</h3>
        </div>
        <span
          className="px-2 py-0.5 rounded-lg text-xs font-semibold"
          style={{
            backgroundColor: color + '20',
            color: color,
          }}
        >
          {tasks.length}
        </span>
      </div>

      <div ref={setNodeRef} className="min-h-[100px] flex flex-col gap-3">
        <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} onClick={onTaskClick} />
          ))}
        </SortableContext>
      </div>

      <button
        onClick={() => onAddTask?.(id)}
        className={`w-full py-2.5 mt-2 border-2 border-dashed ${buttonClass} rounded-xl text-sm transition-colors`}
      >
        + Agregar tarea
      </button>
    </div>
  );
}

KanbanColumn.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  tasks: PropTypes.array.isRequired,
  onAddTask: PropTypes.func,
  onTaskClick: PropTypes.func,
  isDropDisabled: PropTypes.bool,
};
